import React from 'react';
import {
  ToggleLeft,
  NumberNine,
  ListBullets,
  TextT,
  PencilSimple,
} from '@phosphor-icons/react';
import { cn } from 'src/utils/className';
import { Insight, InsightType } from 'src/interfaces/agent.interface';

type PostCallDataExtractionCardProps = {
  insight: Insight;
  onClick: () => void;
  disabled?: boolean;
};

const iconByType: Record<InsightType, React.ReactNode> = {
  [InsightType.BOOLEAN]: <ToggleLeft className="w-4 h-4" weight="fill" />,
  [InsightType.NUMBER]: <NumberNine className="w-4 h-4" weight="fill" />,
  [InsightType.ENUM]: <ListBullets className="w-4 h-4" weight="fill" />,
  [InsightType.STRING]: <TextT className="w-4 h-4" weight="fill" />,
};

export const PostCallDataExtractionCardSkeleton: React.FC = () => {
  return (
    <div className="px-4 py-3 rounded-lg border border-neutral-200 bg-neutral-0 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded bg-neutral-100 w-8 h-8" />
        <div className="flex-1 min-w-0 flex items-center gap-4">
          <div className="h-4 w-24 rounded bg-neutral-100" />
          <div className="h-3 w-32 rounded bg-neutral-100" />
        </div>
      </div>
    </div>
  );
};

export const PostCallDataExtractionCard: React.FC<
  PostCallDataExtractionCardProps
> = ({ insight, onClick, disabled = false }) => {
  return (
    <div
      data-testid={`card-data-extraction-${insight.id}`}
      onClick={disabled ? undefined : onClick}
      className={cn(
        'px-4 py-3 rounded-lg border border-neutral-200 bg-neutral-0',
        'transition-all duration-150',
        disabled
          ? 'opacity-60 cursor-not-allowed'
          : 'cursor-pointer hover:border-neutral-300 hover:shadow-sm',
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn('p-2 rounded', 'bg-primary-100 text-primary-500')}>
          {iconByType[insight.type]}
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-4">
          <div className="flex items-center gap-2 min-w-0 shrink-0">
            <h3
              data-testid={`text-data-extraction-label-${insight.id}`}
              className="text-body font-medium text-primary-body truncate"
            >
              {insight.label || insight.id}
            </h3>
          </div>
          <div className="flex items-center gap-3 text-description text-neutral-400 truncate">
            <span className="truncate">{insight.id}</span>
          </div>
        </div>
        <div className="text-neutral-400">
          <PencilSimple size={20} />
        </div>
      </div>
    </div>
  );
};
